import { fromJS, List, Map } from 'immutable';
// See "Storing compressed data into localStorage" at https://pieroxy.net/blog/pages/lz-string/guide.html
// for info about storing compressed strings in localStorage.
// Short version is that we must use compressToUTF16 and decompressFromUTF16 to make it work in IE, Edge
// and Firefox.
import md5 from 'js-md5';
import { compressToUTF16, decompress, decompressFromUTF16 } from 'lz-string';
import moment from 'moment';

import appConfig from 'app-config';
import {
    getTelavoxFormattedLanguageCode,
    isEmptyNullOrUndefined,
    ISO_8601_MS_RESOLUTION,
    keyIn,
} from 'flow/utils';
import { browserSupportStorage } from 'flow/utils/browser-storage-utils';
import { IS_UAE_HOST } from 'flow/utils/browser-utils-TW';
import { clearConsentCookies } from 'flow/utils/cookie-utils';
import {
    IncomingCallSetting,
    PlayRingtoneSetting,
} from 'flow/utils/call-utils';
import {
    addElectronLogRow,
    clearElectronLog,
    getElectronLog,
} from 'flow/utils/electron-log-utils';
import wrappedLocalStorage from 'flow/utils/local-storage-utils';
import {
    MICROPHONE,
    permissionTypes,
    probeMediaPermission,
} from 'flow/utils/media-utils';
import { DisplayMode } from 'flow/utils/rtc-utils';
import {
    removeSelectedOperator,
    removeSelectedQueue,
} from 'flow/utils/supervisor-utils';
import isElectron from 'is-electron';

const AUTOMATICALLY_SEND_CLIENT_LOGS = 'automaticallySendClientLogs';
const RECENT_COLLEAGUES = 'recent';
const LOG = 'log';
export const IDENTITY = 'identity';
const CALL_METHOD = 'callMethod';
export const CALL_CANVAS_SIZE = 'callCanvasSize';
const AUTOANSWER_IP_PHONE = 'autoAnswerWithIpPhone';
export const REGISTER_SOFTPHONE = 'registerSoftphone';
const MAKE_CALLS_ENABLED = 'makeCallsEnabled';
const PLAY_RINGTONE_SETTING = 'playRingtoneSetting';
const DEPARTMENTS_OPEN = 'departmentsOpen';
const PBX_OPEN = 'pbxOpen';
export const INCOMING_CALL_SETTING = 'incomingCallSetting';
export const REDUX_STATE_DEPRECATED = 'reduxState';
export const REDUX_STATE = 'reduxStateUTF16';
const HEADSET_CONF = 'headsetConf';
const PREFERRED_MICROPHONE_ID = 'preferredMicrophoneId';
const PREFERRED_AUDIO_OUTPUT_ID = 'preferredAudioOutputId';
const PREFERRED_RINGTONE_AUDIO_OUTPUT_ID = 'preferredRingtoneAudioOutputId';
const PREFERRED_VIDEO_INPUT_ID = 'preferredVideoInputId';
const NOISE_SUPPRESSION_ENGINE = 'noiseSuppressionEngine';
const PREFERRED_AUDIO_OUTPUT_VOLUME = 'preferredAudioOutputVolume';
const PREFERRED_RINGTONE_VOLUME = 'preferredRingtoneVolume';
const AUDIO_DEVICE_DATA = 'audioDeviceData';
const ADMIN_LOCATION = 'adminLocation';
const CHAT_SHORTCUTS = 'chatShortcuts';
// This install's own copy of whether it is the account's designated companion, so a command
// arriving before the config fetch can be answered synchronously. Home owns the designation
// itself; a copy that outlives the login it was written for is corrected by
// reconcileUndesignatedAccount at boot.
const USE_AS_COMPANION = 'useAsCompanion';
// Whether this surface hands its calls to the desktop app when one is available. Per surface, not
// per user: it describes how the person at this machine is set up right now, so a Teams tab and the
// ordinary web app can answer it differently. See getHandoverToDesktop.
const USE_COMPANION_FOR_CALLS = 'useCompanionForCalls';
const VIDEO_CONFERENCE_WITH_SIP = 'videoConferenceWithSip';
const CALL_WAITING_ENABLED = 'callWaitingEnabled';
const SHOW_LOCAL_VIDEO_IN_GRID = 'showLocalVideoInGrid';
const SHOW_INCOMING_VIDEO = 'showIncomingVideo';
const CENTRAL_SWITCH_WIDGETS = 'centralSwitchWidgets';
const BLIND_TRANSFER_SETTINGS = 'blindTransferSettings';
const USER_HAS_SET_DISPLAY_TICKETS_TAB_TOGGLE_TO_ON =
    'userHasSetDisplayTicketsTabToggleToOn';
export const TICKET_FAILED_EVENTS_LOCAL_STORAGE_KEY = 'ticketFailedEvents';
const VIDEO_BACKGROUND_PREFERENCES = 'videoBackgroundPreferences';
export const COLISTENING_WIDGET_USERS = 'CoListeningWidgetUsers';

const REDACTED_KEYS = [
    'email',
    'lastname',
    'mobile',
    'mobileNumber',
    'name',
    'password',
    'personalNumber',
    'phone',
    'phoneNumber',
    'secret',
    'ssn',
    'surname',
    'username',
];

const pattern = new RegExp(
    `"(${REDACTED_KEYS.join('|')})" ?[:=] ?"(.*?)"`,
    'gi',
);

export const redact = (entry) => {
    if (typeof entry === 'object') {
        const clonedEntry = { ...entry };
        Object.entries(clonedEntry).forEach(([key]) => {
            if (REDACTED_KEYS.includes(key)) {
                clonedEntry[key] = 'REDACTED';
            }
        });

        return clonedEntry;
    }

    if (typeof entry !== 'string') return entry;

    return entry.replace(pattern, '"$1":"REDACTED"');
};

const maxLogSizeInBytes = 100000;

export const anyOptionalKeyDown = (e) =>
    e.shiftKey || e.metaKey || e.altKey || e.ctrlKey;

export const setInstanceId = (number) => {
    const instanceId = md5(
        number + '-' + moment().locale('en').format(ISO_8601_MS_RESOLUTION),
    );
    wrappedLocalStorage.setItem('instanceId', instanceId);
};

export const removePersonalInformationIfNewUser = (
    identifier,
    newUserCallback,
) => {
    if (!identifier) {
        return;
    }

    if (!wrappedLocalStorage.isSet(IDENTITY)) {
        wrappedLocalStorage.setItem(IDENTITY, JSON.stringify(identifier));
        newUserCallback();
        return;
    }

    const oldIdentity = JSON.parse(wrappedLocalStorage.getItem(IDENTITY));

    // String() guards against a type-only mismatch (e.g. one caller passing a
    // number, another a string for the same identity) falsely reading as an
    // identity switch and wiping consent/logs for no real reason.
    if (String(oldIdentity) !== String(identifier)) {
        clearLog();
        removeSelectedOperator();
        removeSelectedQueue();
        wrappedLocalStorage.removeItem(INCOMING_CALL_SETTING);
        wrappedLocalStorage.removeItem(PLAY_RINGTONE_SETTING);
        wrappedLocalStorage.removeItem(REGISTER_SOFTPHONE);
        wrappedLocalStorage.removeItem(MAKE_CALLS_ENABLED);
        wrappedLocalStorage.removeItem(
            USER_HAS_SET_DISPLAY_TICKETS_TAB_TOGGLE_TO_ON,
        );
        wrappedLocalStorage.removeItem(TICKET_FAILED_EVENTS_LOCAL_STORAGE_KEY);
        wrappedLocalStorage.removeItem(REDUX_STATE);
        wrappedLocalStorage.removeItem(COLISTENING_WIDGET_USERS);
        // The companion settings are equally the previous user's, but cannot be dropped here:
        // the same user logging back in must keep the machine they designated, and only the
        // account's designation says which case this is. Cleared against it at boot instead, in
        // reconcileUndesignatedAccount. Nothing acts on either before that answer arrives, because
        // both companion entry points also require the feature switch, which starts off.
        wrappedLocalStorage.setItem(IDENTITY, JSON.stringify(identifier));
        // Only on an actual identity switch, not first-ever login, so a fresh
        // pre-login consent choice isn't wiped the moment that user logs in.
        clearConsentCookies();

        newUserCallback();
    }
};

export const hasLogs = async () => {
    if (isElectron()) {
        try {
            const logs = await getElectronLog();
            return !!logs;
        } catch (_error) {
            return false;
        }
    }

    const logs = wrappedLocalStorage.getItem(LOG);
    return logs && logs.length > 0;
};

export const getLog = async (shouldClearLog) => {
    if (isElectron()) {
        return await getElectronLog(shouldClearLog);
    }

    const log = Promise.resolve(JSON.parse(wrappedLocalStorage.getItem(LOG)));

    if (shouldClearLog) {
        wrappedLocalStorage.removeItem(LOG);
    }

    return log;
};

export const addTicketLogEntry = (...logRow) => {
    addLogEntry('TICKET', logRow);
};

export const addOmniChannelLogEntry = (...logRow) => {
    addLogEntry('OMNICHANNEL', logRow);
};

export const addVideoLogEntry = (...logRow) => {
    addLogEntry('VIDEO', logRow);
};

export const addAudioLogEntry = (...logRow) => {
    addLogEntry('AUDIO', logRow);
};

export const addPhoneLogEntry = (...logRow) => {
    addLogEntry('PHONE', logRow);
};

export const addHeadsetLogEntry = (...logRow) => {
    addLogEntry('HEADSET', logRow);
};

export const addTopLogEntry = (...logRow) => {
    addLogEntry('TOP', logRow);
};

export const addGeneralLogEntry = (...logRow) => {
    addLogEntry('GENERAL', logRow);
};

export const addMainProcessLogEntry = (...logRow) => {
    addLogEntry('MAIN', logRow);
};

export const addLocationLogEntry = (...logRow) => {
    addLogEntry('LOCATION', logRow);
};

export const addDeviceLogEntry = (...logRow) => {
    addLogEntry('DEVICE', logRow);
};

export const addUserPhoneLogEntry = (...logRow) => {
    addLogEntry('USER_PHONE', logRow);
};

export const addChatLogEntry = (...logRow) => {
    addLogEntry('CHAT', logRow);
};

export const addHomeLogEntry = (...logRow) => {
    addLogEntry('HOME', logRow);
};

export const userLogEntry = (...logRow) => {
    addLogEntry('USER', logRow);
};

export const addCtiLogEntry = (...logRow) => {
    addLogEntry('CTI', logRow);
};

export const addKuandoLogEntry = (...logRow) => {
    addLogEntry('KUANDO', logRow);
};

export const addSupervisorLogEntry = (...logRow) => {
    addLogEntry('SUPERVISOR', logRow);
};

export const clearLog = () => {
    if (isElectron()) {
        clearElectronLog();
    } else {
        wrappedLocalStorage.removeItem(LOG);
    }
};

export const addLogEntry = (logType, logRow) => {
    logRow = Array.isArray(logRow) ? logRow.map(redact) : redact(logRow);

    const log = logRow
        .map((log) =>
            typeof log === 'object' ? JSON.stringify(log, null, 2) : log,
        )
        .join('');

    const logWithType = `[${logType}] ${log}`;
    if (appConfig.dev && logType !== 'PHONE') {
        console.info(logType, ...logRow);
    }
    const instanceId = getInstanceId();

    if (!instanceId) {
        // If we don't have an instanceId the api call to save the logs will fail
        // so we warn about it in the console and skip the log
        console.error(
            `Instance id is not set. Row of type '${logType}' not added to log.`,
        );
        return;
    }

    const row = {
        instanceId: getInstanceId(),
        version: appConfig.version,
        data:
            moment().locale('en').format(ISO_8601_MS_RESOLUTION) +
            ' ' +
            logWithType +
            '\n',
    };

    if (isElectron()) {
        addElectronLogRow(row);
        return;
    }

    const hasLog = wrappedLocalStorage.getItem(LOG);

    if (!hasLog) {
        wrappedLocalStorage.setItem(LOG, JSON.stringify([]));
    }

    const rawLog = wrappedLocalStorage.getItem(LOG);

    // WEB-9321 Sometimes we fail to set the log in the storage, and since that error is
    // caught we can end up without a log here. Hence the if-check to avoid a type error.
    if (rawLog) {
        const parsedLog = JSON.parse(rawLog);
        parsedLog.push(row);
        while (isFull(parsedLog)) {
            parsedLog.shift();
        }

        wrappedLocalStorage.setItem(LOG, JSON.stringify(parsedLog));
    }
};

export const getInstanceId = () => {
    return wrappedLocalStorage.getItem('instanceId');
};

export const isFull = (logArray) => {
    return maxLogSizeInBytes - lengthInUtf8Bytes(JSON.stringify(logArray)) < 0;
};

export const lengthInUtf8Bytes = (str) => {
    const encoded = encodeURIComponent(str).match(/%[89ABab]/g);
    return str.length + (encoded ? encoded.length : 0);
};

export const isEdge = () => {
    return /Edge/.test(navigator.userAgent);
};

export const isChrome = () => {
    return /Chrome/.test(navigator.userAgent) && !isEdge();
};

export const isChromeOrElectron = () => {
    return isChrome() || isElectron();
};

export const isFirefox = () => {
    return /Firefox/.test(navigator.userAgent) && !isEdge();
};

export const isSafari = () => {
    const browserString = navigator.userAgent.toLowerCase();
    return /safari/.test(browserString) && !/chrome/.test(browserString);
};

export const hasNetworkConnection = () => {
    return navigator.onLine;
};

export const setRecentColleague = (extensionKey) => {
    const prevState = getRecentColleagues();

    if (browserSupportStorage()) {
        if (isEmptyNullOrUndefined(prevState)) {
            const keys = { keyStore: [extensionKey] };
            wrappedLocalStorage.setItem(
                RECENT_COLLEAGUES,
                JSON.stringify(keys),
            );
        } else {
            saveUserToStore(extensionKey);
        }
    }
    return null;
};

export const getRecentColleagues = () => {
    if (browserSupportStorage()) {
        return JSON.parse(wrappedLocalStorage.getItem(RECENT_COLLEAGUES));
    } else {
        return null;
    }
};

const saveUserToStore = (extensionKey) => {
    const prevState = getRecentColleagues();
    const index = prevState.keyStore.indexOf(extensionKey);

    if (index === -1) {
        //If the user doesn't exist in the list place it first
        prevState.keyStore.unshift(extensionKey);
        if (prevState.keyStore.length > 6) {
            prevState.keyStore.splice(6);
        }
        wrappedLocalStorage.setItem(
            RECENT_COLLEAGUES,
            JSON.stringify(prevState),
        );
    } else {
        //If the user exists move it to the top of the list.
        prevState.keyStore.splice(index, 1);
        prevState.keyStore.unshift(extensionKey);
        wrappedLocalStorage.setItem(
            RECENT_COLLEAGUES,
            JSON.stringify(prevState),
        );
    }
};

export const saveHeadsetEnabledToStorage = (extensionKey, value) => {
    if (!browserSupportStorage()) {
        return false;
    }
    const headsetConf = wrappedLocalStorage.getItem(HEADSET_CONF)
        ? JSON.parse(wrappedLocalStorage.getItem(HEADSET_CONF))
        : {};
    headsetConf[extensionKey] = value;
    wrappedLocalStorage.setItem(HEADSET_CONF, JSON.stringify(headsetConf));
    return true;
};

export const getHeadsetEnabledFromStorage = (extensionKey) => {
    if (!browserSupportStorage()) {
        return null;
    }
    const headsetConf = wrappedLocalStorage.getItem(HEADSET_CONF)
        ? JSON.parse(wrappedLocalStorage.getItem(HEADSET_CONF))
        : {};
    return headsetConf[extensionKey];
};

// A stored value that is not a JSON object must not throw during render, so it
// counts as nothing stored.
const readOpenStates = (storageKey) => {
    try {
        const openStates = JSON.parse(wrappedLocalStorage.getItem(storageKey));
        return openStates && typeof openStates === 'object' ? openStates : {};
    } catch (_error) {
        return {};
    }
};

export const saveDepartmentState = (department, isOpen) => {
    if (browserSupportStorage()) {
        const departmentStates = readOpenStates(DEPARTMENTS_OPEN);
        departmentStates[department] = isOpen;
        wrappedLocalStorage.setItem(
            DEPARTMENTS_OPEN,
            JSON.stringify(departmentStates),
        );
    }
};

export const getDepartmentState = (department) => {
    if (browserSupportStorage()) {
        const departmentStates = readOpenStates(DEPARTMENTS_OPEN);
        return Object.prototype.hasOwnProperty.call(
            departmentStates,
            department,
        )
            ? departmentStates[department]
            : true;
    }
    return true;
};

export const savePbxState = (department, isOpen) => {
    if (browserSupportStorage()) {
        const departmentStates = readOpenStates(PBX_OPEN);
        departmentStates[department] = isOpen;
        wrappedLocalStorage.setItem(PBX_OPEN, JSON.stringify(departmentStates));
    }
};

export const getPbxState = (department) => {
    if (browserSupportStorage()) {
        const departmentStates = readOpenStates(PBX_OPEN);
        return Object.prototype.hasOwnProperty.call(
            departmentStates,
            department,
        )
            ? departmentStates[department]
            : true;
    }
    return true;
};

export const saveQueueStatisticsFavorites = (queueKey, favorites) => {
    if (browserSupportStorage()) {
        const queueStatisticsFavourite = wrappedLocalStorage.isSet(
            'queueStatisticsFavourites',
        )
            ? JSON.parse(
                  wrappedLocalStorage.getItem('queueStatisticsFavourites'),
              )
            : {};
        queueStatisticsFavourite[queueKey] = favorites;
        wrappedLocalStorage.setItem(
            'queueStatisticsFavourites',
            JSON.stringify(queueStatisticsFavourite),
        );
    }
};

export const getQueueStatisticsFavorites = (queueKey) => {
    if (browserSupportStorage()) {
        const queueStatisticsFavourite = wrappedLocalStorage.isSet(
            'queueStatisticsFavourites',
        )
            ? JSON.parse(
                  wrappedLocalStorage.getItem('queueStatisticsFavourites'),
              )
            : {};
        return Object.prototype.hasOwnProperty.call(
            queueStatisticsFavourite,
            queueKey,
        )
            ? queueStatisticsFavourite[queueKey]
            : null;
    }
    return [];
};

export const saveChannelStatisticsFavorites = (channelKey, favorites) => {
    if (browserSupportStorage()) {
        const channelStatisticsFavourite = wrappedLocalStorage.isSet(
            'channelStatisticsFavorites',
        )
            ? JSON.parse(
                  wrappedLocalStorage.getItem('channelStatisticsFavorites'),
              )
            : {};
        channelStatisticsFavourite[channelKey] = favorites;
        wrappedLocalStorage.setItem(
            'channelStatisticsFavorites',
            JSON.stringify(channelStatisticsFavourite),
        );
    }
};

export const getChannelStatisticsFavorites = (channelKey) => {
    if (browserSupportStorage()) {
        const channelStatisticsFavourite = wrappedLocalStorage.isSet(
            'channelStatisticsFavorites',
        )
            ? JSON.parse(
                  wrappedLocalStorage.getItem('channelStatisticsFavorites'),
              )
            : {};
        return Object.prototype.hasOwnProperty.call(
            channelStatisticsFavourite,
            channelKey,
        )
            ? channelStatisticsFavourite[channelKey]
            : null;
    }
    return [];
};

export const getSoftphoneShouldBeRegistered = (loggedInWithTempPass) => {
    if (loggedInWithTempPass) {
        return false;
    }

    if (!browserSupportStorage()) {
        return false;
    }

    if (wrappedLocalStorage.isSet(REGISTER_SOFTPHONE)) {
        return JSON.parse(wrappedLocalStorage.getItem(REGISTER_SOFTPHONE));
    }

    if (wrappedLocalStorage.isSet(INCOMING_CALL_SETTING)) {
        const incomingCallSetting = IncomingCallSetting.get(
            wrappedLocalStorage.getItem(INCOMING_CALL_SETTING),
        );

        if (
            incomingCallSetting === IncomingCallSetting.RING ||
            incomingCallSetting === IncomingCallSetting.SILENT
        ) {
            return true;
        }
    }

    //If Desktop app and no preference is set, and user is not logged in with temporary password,
    //default to true
    if (isElectron()) {
        return !loggedInWithTempPass;
    }

    return !loggedInWithTempPass && IS_UAE_HOST;
};

export const saveSoftphoneShouldBeRegistered = (shouldBeRegistered) => {
    if (browserSupportStorage()) {
        wrappedLocalStorage.setItem(REGISTER_SOFTPHONE, shouldBeRegistered);
    }
};

// The "Make calls" toggle on this client. Kept apart from REGISTER_SOFTPHONE on purpose: that key
// decides whether the softphone is registered, which is what lets this client receive calls, so
// turning off outgoing calls must not touch it (AC-1902). Defaults to on until the user turns it off.
export const saveMakeCallsEnabled = (makeCallsEnabled) => {
    if (browserSupportStorage()) {
        wrappedLocalStorage.setItem(MAKE_CALLS_ENABLED, makeCallsEnabled);
    }
};

export const getMakeCallsEnabled = () => {
    if (
        browserSupportStorage() &&
        wrappedLocalStorage.isSet(MAKE_CALLS_ENABLED)
    ) {
        return JSON.parse(wrappedLocalStorage.getItem(MAKE_CALLS_ENABLED));
    }
    return true;
};

export const isSoftphoneExplicitlyDisabled = () => {
    if (!browserSupportStorage()) return false;
    if (wrappedLocalStorage.isSet(REGISTER_SOFTPHONE)) {
        return !JSON.parse(wrappedLocalStorage.getItem(REGISTER_SOFTPHONE));
    }
    return false;
};

export const getPreferredRingtoneVolume = () => {
    if (
        browserSupportStorage() &&
        wrappedLocalStorage.isSet(PREFERRED_RINGTONE_VOLUME)
    ) {
        return JSON.parse(
            wrappedLocalStorage.getItem(PREFERRED_RINGTONE_VOLUME),
        );
    }

    return 1;
};

export const savePreferredRingtoneVolume = (volume) => {
    if (browserSupportStorage()) {
        wrappedLocalStorage.setItem(PREFERRED_RINGTONE_VOLUME, volume);
    }
};

export const getPreferredAudioOutputVolume = () => {
    if (
        browserSupportStorage() &&
        wrappedLocalStorage.isSet(PREFERRED_AUDIO_OUTPUT_VOLUME)
    ) {
        return JSON.parse(
            wrappedLocalStorage.getItem(PREFERRED_AUDIO_OUTPUT_VOLUME),
        );
    }

    return 1;
};

export const savePreferredAudioOutputVolume = (volume) => {
    if (browserSupportStorage()) {
        wrappedLocalStorage.setItem(PREFERRED_AUDIO_OUTPUT_VOLUME, volume);
    }
};

export const getPlayRingtoneSetting = () => {
    if (!browserSupportStorage()) {
        return PlayRingtoneSetting.RING;
    }

    if (wrappedLocalStorage.isSet(PLAY_RINGTONE_SETTING)) {
        return PlayRingtoneSetting.get(
            wrappedLocalStorage.getItem(PLAY_RINGTONE_SETTING),
        );
    }

    if (wrappedLocalStorage.isSet(INCOMING_CALL_SETTING)) {
        const incomingCallSetting = IncomingCallSetting.get(
            wrappedLocalStorage.getItem(INCOMING_CALL_SETTING),
        );

        if (incomingCallSetting === IncomingCallSetting.SILENT) {
            return PlayRingtoneSetting.SILENT;
        }
    }

    return PlayRingtoneSetting.RING;
};

export const savePlayRingtoneSetting = (playRingtoneSetting) => {
    if (browserSupportStorage()) {
        wrappedLocalStorage.setItem(
            PLAY_RINGTONE_SETTING,
            playRingtoneSetting.key,
        );
    }
};

export const savePreferredMicrophoneId = (microphoneId) => {
    if (microphoneId && browserSupportStorage()) {
        wrappedLocalStorage.setItem(PREFERRED_MICROPHONE_ID, microphoneId);
    }
};

export const getPreferredMicrophoneId = () => {
    if (!browserSupportStorage()) {
        return undefined;
    }

    if (wrappedLocalStorage.isSet(PREFERRED_MICROPHONE_ID)) {
        return wrappedLocalStorage.getItem(PREFERRED_MICROPHONE_ID);
    }

    return undefined;
};

export const saveNoiseSuppressionEngine = (engine) => {
    if (engine && browserSupportStorage()) {
        wrappedLocalStorage.setItem(NOISE_SUPPRESSION_ENGINE, engine);
    }
};

export const getNoiseSuppressionEngine = () => {
    if (!browserSupportStorage()) {
        return undefined;
    }

    if (wrappedLocalStorage.isSet(NOISE_SUPPRESSION_ENGINE)) {
        return wrappedLocalStorage.getItem(NOISE_SUPPRESSION_ENGINE);
    }

    return undefined;
};

export const savePreferredVideoInputId = (videoInputId) => {
    if (videoInputId && browserSupportStorage()) {
        wrappedLocalStorage.setItem(PREFERRED_VIDEO_INPUT_ID, videoInputId);
    }
};

export const getPreferredVideoInputId = () => {
    if (!browserSupportStorage()) {
        return undefined;
    }

    if (wrappedLocalStorage.isSet(PREFERRED_VIDEO_INPUT_ID)) {
        return wrappedLocalStorage.getItem(PREFERRED_VIDEO_INPUT_ID);
    }

    return undefined;
};

export const savePreferredAudioOutputId = (audioOutputId) => {
    if (audioOutputId && browserSupportStorage()) {
        wrappedLocalStorage.setItem(PREFERRED_AUDIO_OUTPUT_ID, audioOutputId);
    }
};

export const getPreferredAudioOutputId = () => {
    if (!browserSupportStorage()) {
        return undefined;
    }

    if (wrappedLocalStorage.isSet(PREFERRED_AUDIO_OUTPUT_ID)) {
        return wrappedLocalStorage.getItem(PREFERRED_AUDIO_OUTPUT_ID);
    }

    return undefined;
};

export const saveAudioDeviceDataSentForStatistics = (data) => {
    if (browserSupportStorage()) {
        wrappedLocalStorage.setItem(AUDIO_DEVICE_DATA, JSON.stringify(data));
    }
};

export const getAudioDeviceDataSentForStatistics = () => {
    if (!browserSupportStorage()) {
        return undefined;
    }

    if (wrappedLocalStorage.isSet(AUDIO_DEVICE_DATA)) {
        const data = wrappedLocalStorage.getItem(AUDIO_DEVICE_DATA);
        return JSON.parse(data);
    }

    return undefined;
};

/**
 * @param audioOutputId saved to storage UNLESS we pass null/undefined. In that case we no longer want
 * a preferred ringtone audio output id to be saved, and thus remove the item from storage.
 */

export const savePreferredRingtoneAudioOutputId = (audioOutputId) => {
    if (!browserSupportStorage()) {
        return;
    }

    if (audioOutputId) {
        wrappedLocalStorage.setItem(
            PREFERRED_RINGTONE_AUDIO_OUTPUT_ID,
            audioOutputId,
        );
    } else if (wrappedLocalStorage.isSet(PREFERRED_RINGTONE_AUDIO_OUTPUT_ID)) {
        wrappedLocalStorage.removeItem(PREFERRED_RINGTONE_AUDIO_OUTPUT_ID);
    }
};

export const getPreferredRingtoneAudioOutputId = () => {
    if (
        !browserSupportStorage() &&
        !wrappedLocalStorage.isSet(PREFERRED_RINGTONE_AUDIO_OUTPUT_ID)
    ) {
        return undefined;
    }

    return wrappedLocalStorage.getItem(PREFERRED_RINGTONE_AUDIO_OUTPUT_ID);
};

export const saveAutomaticallySendClientLogs = (enabled) => {
    if (browserSupportStorage()) {
        wrappedLocalStorage.setItem(AUTOMATICALLY_SEND_CLIENT_LOGS, enabled);
    }
};

export const getAutomaticallySendClientLogs = () => {
    if (
        browserSupportStorage() &&
        wrappedLocalStorage.isSet(AUTOMATICALLY_SEND_CLIENT_LOGS)
    ) {
        return JSON.parse(
            wrappedLocalStorage.getItem(AUTOMATICALLY_SEND_CLIENT_LOGS),
        );
    }

    return true;
};

export const saveApplicationLanguage = (language) => {
    if (browserSupportStorage()) {
        wrappedLocalStorage.setItem('applicationLanguage', language);
    }
};

export const translateNumber = (number) => {
    const lang = document.documentElement.lang;

    if (lang === 'ar') {
        return number.toLocaleString('ar-u-nu-arab');
    }

    return number.toLocaleString(document.documentElement.lang);
};

export const translateTime = (number) => {
    const lang = document.documentElement.lang;

    if (lang.toLowerCase() === 'ar') {
        return number.replace(/\d/g, (d) => '٠١٢٣٤٥٦٧٨٩'[d]);
    }

    return number;
};

export const addLanguageTohtml = (language) => {
    document.documentElement.lang = language;
};

export const setLanguageDirection = (locale) => {
    if (locale?.direction) {
        document.documentElement.dir = locale.direction;
    }
};

export const getApplicationLanguage = () => {
    if (
        !browserSupportStorage() ||
        !wrappedLocalStorage.isSet('applicationLanguage')
    ) {
        return getTelavoxFormattedLanguageCode(window.navigator.language);
    }

    return wrappedLocalStorage.getItem('applicationLanguage');
};

export const saveSpokenLanguage = (language) => {
    if (browserSupportStorage()) {
        wrappedLocalStorage.setItem('spokenLanguage', language);
    }
};

export const getSpokenLanguage = () => {
    if (!browserSupportStorage()) {
        return (
            window.navigator.language && window.navigator.language.split('-')[0]
        );
    }

    return wrappedLocalStorage.isSet('spokenLanguage')
        ? wrappedLocalStorage.getItem('spokenLanguage')
        : null;
};

export const getSignalIosMenuFinishedLoading = () => {
    if (!browserSupportStorage()) {
        return false;
    }

    return wrappedLocalStorage.isSet('signalIosMenuFinishedLoading');
};

export const saveCallMethod = (method) => {
    if (!browserSupportStorage()) {
        return;
    }

    wrappedLocalStorage.setItem(CALL_METHOD, method);
};

export const getCallMethod = () => {
    return browserSupportStorage() && wrappedLocalStorage.isSet(CALL_METHOD)
        ? wrappedLocalStorage.getItem(CALL_METHOD)
        : null;
};

/**
 * Forget the stored call method, putting the user back on the same footing as one who has never
 * chosen. Distinct from saving a method: there is no stored value meaning "unchosen", and the
 * default in fetchCompanionDevices keys off its absence.
 */
export const clearCallMethod = () => {
    if (!browserSupportStorage()) {
        return;
    }

    wrappedLocalStorage.removeItem(CALL_METHOD);
};

export const savePreferredCallCanvasSize = (size) => {
    if (!browserSupportStorage()) {
        return;
    }

    wrappedLocalStorage.setItem(CALL_CANVAS_SIZE, size);
};

export const getPreferredCallCanvasSize = () => {
    return browserSupportStorage() &&
        wrappedLocalStorage.isSet(CALL_CANVAS_SIZE)
        ? wrappedLocalStorage.getItem(CALL_CANVAS_SIZE)
        : DisplayMode.THUMBNAIL.key;
};

export const saveAutoAnswerWithIpPhone = (autoAnswer) => {
    if (!browserSupportStorage()) {
        return;
    }

    wrappedLocalStorage.setItem(AUTOANSWER_IP_PHONE, autoAnswer);
};

export const getAutoAnswerWithIpPhone = () => {
    if (
        browserSupportStorage() &&
        wrappedLocalStorage.isSet(AUTOANSWER_IP_PHONE)
    ) {
        return JSON.parse(wrappedLocalStorage.getItem(AUTOANSWER_IP_PHONE));
    }

    return false;
};

export const getAdminLocationFromStorage = () => {
    if (!browserSupportStorage()) {
        return '';
    }
    if (wrappedLocalStorage.isSet(ADMIN_LOCATION)) {
        return JSON.parse(wrappedLocalStorage.getItem(ADMIN_LOCATION));
    }
    return '';
};

/**
 * @param {string} The location at which we find the admin pages
 */
export const setAdminLocationToStorage = (adminLocation) => {
    if (!browserSupportStorage()) {
        return;
    }
    wrappedLocalStorage.setItem(ADMIN_LOCATION, JSON.stringify(adminLocation));
};

export const clearAdminLocationFromStorage = () => {
    if (!browserSupportStorage()) {
        return;
    }
    wrappedLocalStorage.removeItem(ADMIN_LOCATION);
};

const filterStateToStore = (state) => {
    const queueMembers = state.pbx.get('queueMembers', Map());

    return {
        pbx: state.pbx
            .filter(keyIn('queues', 'refers', 'conferences', 'connections'))
            .update('queues', Map(), (queues) => {
                // Merge queueMembers back into each queue for backward compatibility
                // with base branch that expects queueMembers nested inside queue objects.
                // This so that if a revert was to be made, or a swap to an older branch is made during development,
                // it would still work without typeError in the reducer when filtering queueMembers.
                return queues.map((queue) => {
                    const queueKey = queue.get('key');
                    const members = queueMembers.get(queueKey, List());
                    return queue
                        .filter(keyIn('key', 'description', 'number'))
                        .set('queueMembers', members);
                });
            })
            .update('voicemails', Map(), (voicemails) => {
                return voicemails.map((voicemail) =>
                    voicemail.filter(
                        keyIn('key', 'description', 'number', 'settings'),
                    ),
                );
            }),
        extensions: state.extensions
            .filter(keyIn('extensions'))
            .update('extensions', (extensions) => {
                return extensions.map((extension) =>
                    extension.filter(
                        keyIn('key', 'contact', 'admin', 'chatUserKey'),
                    ),
                );
            }),
    };
};

export const saveReduxState = (state) => {
    if (!browserSupportStorage()) {
        return;
    }

    wrappedLocalStorage.setItem(
        REDUX_STATE,
        compressToUTF16(JSON.stringify(filterStateToStore(state))),
    );
};

const getReduxStateDeprecated = () => {
    if (
        !browserSupportStorage() ||
        !wrappedLocalStorage.isSet(REDUX_STATE_DEPRECATED)
    ) {
        return undefined;
    }

    const serializedState = wrappedLocalStorage.getItem(REDUX_STATE_DEPRECATED);

    // We have changed the key under which we store the redux state in localStorage from
    // reduxState to reduxStateUTF16 since we are using compressToUTF16 instead of compress
    // (both from lz-string) to compress the data, to make it work in IE, Edge and Firefox.
    // Because of this there might be old redux state data storead under the old key and
    // if there is we have to remove that.
    wrappedLocalStorage.removeItem(REDUX_STATE_DEPRECATED);

    try {
        if (serializedState === null) {
            return undefined;
        }
        const data = JSON.parse(decompress(serializedState));
        const rootObject = {};
        for (const key in data) {
            rootObject[key] = fromJS(data[key]);
        }
        return rootObject;
    } catch (error) {
        console.error('Failed to deserialize cache, error ' + error);
        return undefined;
    }
};

/**
 * Migrate pbx state from old format (queueMembers nested in queues) to new format (queueMembers at top level).
 * This ensures backward compatibility when loading state saved by the base branch.
 */
const migratePbxState = (pbxState) => {
    if (!pbxState) return pbxState;

    // If queueMembers already exists at top level, no migration needed
    if (
        pbxState.has('queueMembers') &&
        pbxState.get('queueMembers')?.size > 0
    ) {
        return pbxState;
    }

    const queues = pbxState.get('queues', Map());
    let queueMembers = Map();

    // Extract queueMembers from each queue and build top-level queueMembers map
    const migratedQueues = queues.map((queue) => {
        const queueKey = queue.get('key');
        const members = queue.get('queueMembers');
        if (members) {
            queueMembers = queueMembers.set(queueKey, members);
        }
        return queue.delete('queueMembers');
    });

    return pbxState
        .set('queues', migratedQueues)
        .set('queueMembers', queueMembers);
};

export const getReduxState = () => {
    const stateFromDeprecatedKey = getReduxStateDeprecated();

    if (stateFromDeprecatedKey) {
        return stateFromDeprecatedKey;
    }

    if (!browserSupportStorage() || !wrappedLocalStorage.isSet(REDUX_STATE)) {
        return undefined;
    }

    const serializedState = wrappedLocalStorage.getItem(REDUX_STATE);
    try {
        if (serializedState === null) {
            return undefined;
        }
        const data = JSON.parse(decompressFromUTF16(serializedState));
        const rootObject = {};
        for (const key in data) {
            rootObject[key] = fromJS(data[key]);
        }

        // Migrate pbx state to handle queueMembers in old format
        if (rootObject.pbx) {
            rootObject.pbx = migratePbxState(rootObject.pbx);
        }

        return rootObject;
    } catch (error) {
        console.error('Failed to deserialize cache, error ' + error);
        return undefined;
    }
};

export const putAppInFocus = () => {
    const electronRemote = isElectron() ? require('@electron/remote') : null;
    const osIsWindows =
        electronRemote && electronRemote.process.platform === 'win32';

    if (electronRemote && osIsWindows) {
        if (!electronRemote.getCurrentWindow()) {
            return;
        }

        /**
         * Workaround to focus electron app on Windows.
         */

        electronRemote.getCurrentWindow().show();
    } else {
        window.focus();

        sendExtensionNotification();
    }
};

/**
 * From a string on the form '(?)name1=value1&name2=value2...nameN=valueN',
 * find the value belonging to a certain name.
 *
 * The string may or may not start with a '?' character.
 *
 * @param {String} queryString  A query string
 * @param {String} paramName    A name for one of the parameters
 */
export function getQueryParam(queryString, paramName) {
    if (!queryString || !paramName) {
        return;
    }
    const prunedQueryString = queryString.startsWith('?')
        ? queryString.substr(1)
        : queryString;
    const nameValuePairs = prunedQueryString.split('&');
    for (let i = 0; i < nameValuePairs.length; i++) {
        const nameAndValue = nameValuePairs[i].split('=');
        if (!nameAndValue[1]) {
            // This is needed, since if nameAndValue[1] is undefined,
            // decodeURIComponent(nameAndValue[1]) will return a String 'undefined'.
            return;
        }
        if (decodeURIComponent(nameAndValue[0]) === paramName) {
            return decodeURIComponent(nameAndValue[1]); // eslint-disable-line consistent-return
        }
    }
}

export const errorMessageIsPlayRequestInterrupted = (error) => {
    if (!error || !error.message) {
        return false;
    }

    return error.message.includes(
        'The play() request was interrupted by a call to pause()',
    );
};

export const errorMessageIsInteractBeforePlay = (error) => {
    if (!error || !error.message) {
        return false;
    }
    return error.message.includes(
        "play() failed because the user didn't interact with the document first.",
    );
};

export const errorMessageIsRTCPeerConnectionGone = (error) => {
    if (!error || !error.message) {
        return false;
    }
    return error.message.includes('RTCPeerConnection is gone');
};

export const setAutoLogin = (autoLogin) => {
    wrappedLocalStorage.setItem('autoLogin', JSON.stringify(!!autoLogin));
};

export const getAutoLogin = () => {
    return !!JSON.parse(wrappedLocalStorage.getItem('autoLogin'));
};

/**
 * Check user microphone access in Chrome
 */
export const checkBrowserMicrophonePermission = async () => {
    const { navigator } = window;

    const microphonePermission = await navigator.permissions.query({
        name: MICROPHONE,
    });
    if (microphonePermission.state === permissionTypes.PROMPT) {
        try {
            await probeMediaPermission({ audio: true });
        } catch (_error) {
            return false;
        }
    } else if (microphonePermission.state === permissionTypes.DENIED) {
        return false;
    }
    return true;
};

export const saveChatShortcuts = (shortCutOn) => {
    if (!browserSupportStorage()) {
        return;
    }

    wrappedLocalStorage.setItem(CHAT_SHORTCUTS, JSON.stringify(!!shortCutOn));
};

export const getChatShortcuts = () => {
    if (
        !browserSupportStorage() ||
        !wrappedLocalStorage.isSet(CHAT_SHORTCUTS)
    ) {
        return true;
    }

    return wrappedLocalStorage.getItem(CHAT_SHORTCUTS) === 'true';
};

export const saveUseAsCompanion = (useAsCompanion) => {
    if (!browserSupportStorage()) {
        return;
    }

    wrappedLocalStorage.setItem(
        USE_AS_COMPANION,
        JSON.stringify(!!useAsCompanion),
    );
};

/**
 * Whether the user has designated this desktop install as their companion. Defaults to false:
 * an install that has never been opted in must keep behaving exactly as it did before.
 */
export const getUseAsCompanion = () => {
    if (
        !browserSupportStorage() ||
        !wrappedLocalStorage.isSet(USE_AS_COMPANION)
    ) {
        return false;
    }

    return wrappedLocalStorage.getItem(USE_AS_COMPANION) === 'true';
};

/**
 * Whether the user deliberately turned this install off as a companion, as opposed to never having
 * turned it on. Both read as false through getUseAsCompanion, and the difference decides whether a
 * boot may promote this machine back to the role its account still designates - see
 * syncDesignation. Writing false where clearUseAsCompanion is meant would erase that difference.
 */
export const hasUseAsCompanionOptOut = () =>
    browserSupportStorage() &&
    wrappedLocalStorage.isSet(USE_AS_COMPANION) &&
    wrappedLocalStorage.getItem(USE_AS_COMPANION) !== 'true';

/**
 * Stand this install down without recording it as a choice, leaving it as it was before anyone
 * turned it on. For a stand-down the client decided on - the account designates nothing, or another
 * machine - as opposed to the user saying no, which is saveUseAsCompanion(false).
 */
export const clearUseAsCompanion = () => {
    if (!browserSupportStorage()) {
        return;
    }

    wrappedLocalStorage.removeItem(USE_AS_COMPANION);
};

/**
 * Whether this surface hands its calls to the desktop app when one is available.
 *
 * Defaults to on, so handover works without setup - the user should not have to find a setting
 * before their calls land where they are sitting. Turning it off makes the surface own its own
 * media again and behave exactly as it did before the companion existed, which is why the ordinary
 * call methods stay available alongside it rather than being replaced by it.
 */
export const getHandoverToDesktop = () => {
    if (
        !browserSupportStorage() ||
        !wrappedLocalStorage.isSet(USE_COMPANION_FOR_CALLS)
    ) {
        return true;
    }

    return wrappedLocalStorage.getItem(USE_COMPANION_FOR_CALLS) === 'true';
};

export const saveHandoverToDesktop = (handover) => {
    if (!browserSupportStorage()) {
        return;
    }

    wrappedLocalStorage.setItem(
        USE_COMPANION_FOR_CALLS,
        JSON.stringify(!!handover),
    );
};

export const saveVideoConferenceWithSip = (videoConferenceWithSip) => {
    if (!browserSupportStorage()) {
        return;
    }

    wrappedLocalStorage.setItem(
        VIDEO_CONFERENCE_WITH_SIP,
        JSON.stringify(!!videoConferenceWithSip),
    );
};

export const getVideoConferenceWithSip = () => {
    if (
        !browserSupportStorage() ||
        !wrappedLocalStorage.isSet(VIDEO_CONFERENCE_WITH_SIP)
    ) {
        return false;
    }

    return wrappedLocalStorage.getItem(VIDEO_CONFERENCE_WITH_SIP) === 'true';
};
export const saveShowLocalVideoInGrid = (showLocalVideoInGrid) => {
    if (!browserSupportStorage()) {
        return;
    }
    wrappedLocalStorage.setItem(SHOW_LOCAL_VIDEO_IN_GRID, showLocalVideoInGrid);
};

export const getShowLocalVideoInGrid = () => {
    if (
        !browserSupportStorage() ||
        !wrappedLocalStorage.isSet(SHOW_LOCAL_VIDEO_IN_GRID)
    ) {
        return true;
    }
    return wrappedLocalStorage.getItem(SHOW_LOCAL_VIDEO_IN_GRID) === 'true';
};

export const saveShowIncomingVideo = (showIncomingVideo) => {
    if (!browserSupportStorage()) {
        return;
    }
    wrappedLocalStorage.setItem(SHOW_INCOMING_VIDEO, showIncomingVideo);
};

export const getShowIncomingVideo = () => {
    if (
        !browserSupportStorage() ||
        !wrappedLocalStorage.isSet(SHOW_INCOMING_VIDEO)
    ) {
        return true;
    }
    return wrappedLocalStorage.getItem(SHOW_INCOMING_VIDEO) === 'true';
};

export const saveVideoBackgroundPreferences = (preferences) => {
    if (!browserSupportStorage()) {
        return;
    }
    wrappedLocalStorage.setItem(
        VIDEO_BACKGROUND_PREFERENCES,
        JSON.stringify(preferences),
    );
};

export const getVideoBackgroundPreferences = () => {
    if (
        !browserSupportStorage() ||
        !wrappedLocalStorage.isSet(VIDEO_BACKGROUND_PREFERENCES)
    ) {
        return null;
    }
    try {
        return JSON.parse(
            wrappedLocalStorage.getItem(VIDEO_BACKGROUND_PREFERENCES),
        );
    } catch (_error) {
        return null;
    }
};

export const hasCallWaitingLocalStorage = () => {
    if (!browserSupportStorage()) {
        return false;
    }

    return wrappedLocalStorage.isSet(CALL_WAITING_ENABLED);
};

export const getCallWaitingEnabledFromLocalStorage = () => {
    if (
        !browserSupportStorage() ||
        !wrappedLocalStorage.isSet(CALL_WAITING_ENABLED)
    ) {
        return false;
    }
    return wrappedLocalStorage.getItem(CALL_WAITING_ENABLED) === 'true';
};

export const clearCallWaitingFromLocalStorage = () => {
    if (
        !browserSupportStorage() ||
        !wrappedLocalStorage.isSet(CALL_WAITING_ENABLED)
    ) {
        return false;
    }
    return wrappedLocalStorage.removeItem(CALL_WAITING_ENABLED) === 'true';
};

export const saveCentralSwitchWidgets = (widgetList) => {
    if (!browserSupportStorage() || !widgetList) {
        return;
    }
    wrappedLocalStorage.setItem(
        CENTRAL_SWITCH_WIDGETS,
        JSON.stringify(widgetList),
    );
};

export const getCentralSwitchWidgets = () => {
    const DefaultWidgets = Object.freeze([
        'dialPad',
        'favoritePBX',
        'RecentContacted',
        'userCalendar',
    ]);

    if (!browserSupportStorage()) {
        return DefaultWidgets;
    } else {
        return wrappedLocalStorage.isSet(CENTRAL_SWITCH_WIDGETS)
            ? JSON.parse(wrappedLocalStorage.getItem(CENTRAL_SWITCH_WIDGETS))
            : DefaultWidgets;
    }
};

export const saveBlindTransferSettings = (setting, value) => {
    if (!browserSupportStorage()) {
        return;
    }
    const blindTransferSettings = wrappedLocalStorage.getItem(
        BLIND_TRANSFER_SETTINGS,
    )
        ? JSON.parse(wrappedLocalStorage.getItem(BLIND_TRANSFER_SETTINGS))
        : {};
    blindTransferSettings[setting] = value;
    wrappedLocalStorage.setItem(
        BLIND_TRANSFER_SETTINGS,
        JSON.stringify(blindTransferSettings),
    );
};

export const getBlindTransferSetting = (setting) => {
    if (!browserSupportStorage()) {
        return null;
    }
    const blindTransferSettings = wrappedLocalStorage.getItem(
        BLIND_TRANSFER_SETTINGS,
    )
        ? JSON.parse(wrappedLocalStorage.getItem(BLIND_TRANSFER_SETTINGS))
        : {};
    return blindTransferSettings[setting];
};

/**
 * Sends a message to the browser extension handling tab navigation
 * in the case the webapp has been iframed as navigating to a tab
 * through the notification popping up does not work otherwise
 * due to CORS.
 */
const sendExtensionNotification = () => {
    if (window.chrome && window.chrome.runtime) {
        window.chrome.runtime.sendMessage(
            'bdoamjmdnbhgodgonekmefjneeohapkj',
            {
                type: 'FOCUS_ME',
            },
            {},
            () => {},
        );
    }
};

export const parseUrlParameter = (parameter) => {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    return urlParams.get(parameter);
};

export const addSipConnectionEstablishedDiv = () => {
    const div = document.createElement('div');
    div.style.display = 'none';
    div.setAttribute('data-test-id', 'sip-connection-established');
    document.body.appendChild(div);
};
